import React from "react";
import { ViewComponentPropType } from "@skyslit/ark-react";
import { Helmet } from "react-helmet-async";
import DefaultModule from "..";
import ReactTable from "react-table";
import { Modal } from "reactstrap";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

type StateType = {
  isModalOpen: boolean;
  data: [];
  pages: number;
  loading: boolean;
  page: number;
  search: string;
};

class SubscriptionPage extends React.Component<
  ViewComponentPropType<DefaultModule>,
  StateType
> {
  private fetchTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      isModalOpen: false,
      data: [],
      pages: 1,
      loading: true,
      page: 0,
      search: "",
    };
    this.toggleModal = this.toggleModal.bind(this);
    this._handleAddSubscription = this._handleAddSubscription.bind(this);
    this._handleFetchSubscription = this._handleFetchSubscription.bind(this);
    this.onFetchData = this.onFetchData.bind(this);
  }

  toggleModal() {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    });
  }

  _handleAddSubscription = (data: any) => {
    this.props.module.services
      .addSubscription(data.title)
      .then((response: any) => {
        this.toggleModal();
        this.props.history.push(`/admin/subscriptions/editor/${response._id}`);
      })
      .catch((e) => {
        this.props.module.showError(
          "Subscription cannot be added",
          e.message ? e.message : "Network error",
          true
        );
      });
  };

  _handleFetchSubscription(page: number = 0, search: string = "") {
    this.setState({ loading: true });
    this.props.module.services
      .getAllSubscriptions({ page, limit: PAGE_SIZE, search })
      .then((response: any) => {
        this.setState({
          data: response?.subscriptions || [],
          pages: response?.pages || 1,
          page: response?.page ?? page,
          search,
          loading: false,
        });
      })
      .catch((e) => {
        this.setState({ loading: false });
        this.props.module.showError(
          "Fetching subscrptions failed",
          e.message ? e.message : "Network error",
          true
        );
      });
  }

  onFetchData(state: any) {
    const titleFilter = (state.filtered || []).find(
      (f: any) => f.id === "title"
    );
    const search = titleFilter?.value ? String(titleFilter.value).trim() : "";
    const searchChanged = search !== this.state.search;
    const page = searchChanged ? 0 : state.page || 0;

    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
    }

    this.fetchTimeout = setTimeout(
      () => {
        this._handleFetchSubscription(page, search);
      },
      searchChanged && search ? 400 : 0
    );
  }

  componentWillUnmount() {
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
    }
  }

  render() {
    const { data, pages, loading } = this.state;
    return (
      <>
        {/* @ts-ignore */}
        <Helmet>
          <title>Subsciption Manager - Lakshya eLearning System</title>
        </Helmet>
        <div className="container-fluid subscriptions-listings-wrapper">
          <div className="row">
            <div className="col-12">
              <div className="sticky-button-wrapper py-4 pr-3 d-flex justify-content-between align-items-center">
                <h4>Subscription Manager</h4>
                <button
                  onClick={this.toggleModal}
                  className="add-subscription-button-wrapper px-5 py-3"
                >
                  Add Subscription
                </button>
              </div>
              <ReactTable
                manual
                minRows={3}
                data={data}
                pages={pages}
                page={this.state.page}
                loading={loading}
                defaultPageSize={PAGE_SIZE}
                pageSizeOptions={[PAGE_SIZE]}
                onFetchData={this.onFetchData}
                columns={[
                  {
                    Header: "Title",
                    accessor: "title",
                    filterable: true,
                    Cell: (row) => (
                      <Link
                        className="text-primary"
                        rel="noopener noreferrer"
                        to={`/admin/subscriptions/editor/${row.original._id}`}
                      >
                        {row.value}
                      </Link>
                    ),
                  },
                  {
                    Header: "Is Free",
                    accessor: "isFree",
                    filterable: false,
                    Cell: (row) => {
                      const isFree: boolean = row.value;
                      if (isFree === true) {
                        return "Yes";
                      } else {
                        if (row.original.freeMode === "lakshya-students") {
                          return "Free for Lakshya Students";
                        }
                        return "No";
                      }
                    },
                  },
                  {
                    Header: "Term",
                    accessor: "subscriptionTerm",
                    filterable: false,
                  },
                  {
                    Header: "Gross Amount",
                    accessor: "grossAmount",
                    filterable: false,
                  },
                  {
                    Header: "GST",
                    accessor: "gstAmount",
                    filterable: false,
                    Cell: (row) => (
                      <span>{`${row.original.gstAmount} (${row.original.gstRate})%`}</span>
                    ),
                  },
                  {
                    Header: "Nett Amount",
                    accessor: "netAmount",
                    filterable: false,
                  },
                ]}
              />
            </div>
          </div>
        </div>
        {/* @ts-ignore */}
        <Modal
          autoFocus={true}
          centered={true}
          toggle={this.toggleModal}
          isOpen={this.state.isModalOpen}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New Subscription</h5>
              <button
                type="button"
                onClick={this.toggleModal}
                className="close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <AddSubscriptionForm
                _handleAddSubscription={this._handleAddSubscription}
                toggleModal={this.toggleModal}
              />
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

function AddSubscriptionForm(props: any) {
  const { register, handleSubmit, errors } = useForm();
  const toggleModal = props.toggleModal;
  const _handleAddSubscription = props._handleAddSubscription;

  return (
    <form onSubmit={handleSubmit(_handleAddSubscription)}>
      <input
        autoFocus={true}
        autoComplete="off"
        name="title"
        ref={register({ required: true })}
        type="text"
        className="form-control"
        placeholder="Enter Subscription Title"
      />
      {errors.title && (
        <small className="text-danger" id="otp-error">
          Please provide a subscription title
        </small>
      )}
      <div className="modal-footer">
        <button type="button" onClick={toggleModal} className="btn btn-default">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Create Subsciption
        </button>
      </div>
    </form>
  );
}

export default SubscriptionPage;
